import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './ImagePreviewModal.css';
import { translate } from 'game/translate';
import { GAEvent, GAEventNames } from 'game/analytics/events';
import { useSelector } from 'react-redux';
import { UIRootState } from 'ui/store/reducers';

interface PreviewText {
  titleKey: string;
  textKey: string;
}

interface StoryImage {
  id: number;
  src: string;
  alt: string;
  previewText?: PreviewText;
  isUnlocked: boolean;
}

interface ImagePreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  images: StoryImage[];
  initialSlideIndex: number;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  isOpen,
  onClose,
  images,
  initialSlideIndex,
}) => {
  const unlockedImages = useMemo<StoryImage[]>(() => images.filter(img => img.isUnlocked), [images]);
  const storyName = useSelector((state: UIRootState) => state.gameUI.modals.imagePreviewModal.storyName);
  const placement = useSelector((state: UIRootState) => state.gameUI.modals.imagePreviewModal.placement);
  

  const [timeMs, setTimeMs] = useState(Date.now());

  const carouselPositionToOriginalIndex = useMemo<Map<number, number>>(() => {
    const map = new Map<number, number>();
    unlockedImages.forEach((img, index) => map.set(index, img.id));
    return map;
  }, [unlockedImages]);
  
  const modalRef = useRef<HTMLDivElement>(null);

  const [currentIndex, setCurrentIndex] = useState(initialSlideIndex);

  const [showMobileText, setShowMobileText] = useState(false);


  useEffect(() => {
    setCurrentIndex(initialSlideIndex);
  }, [initialSlideIndex]);

  useEffect(() => {
    if (isOpen) {
      setShowMobileText(false);
      document.querySelector(`#newStoriesCarousel .stories-carousel-item:nth-of-type(${initialSlideIndex + 1})`)?.classList.add('active');
    }
  }, [isOpen]);

  const handlePrevious = useCallback(() => {
    if (currentIndex > 0) {
      const newIndex = currentIndex - 1;
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, carouselPositionToOriginalIndex]);

  const handleNext = useCallback(() => {
    if (currentIndex < unlockedImages.length - 1) {
      const newIndex = currentIndex + 1;
      setCurrentIndex(newIndex);
    }
  }, [currentIndex, unlockedImages.length, carouselPositionToOriginalIndex]);

  const handleClose = useCallback(() => {
    if (images.find(img => img.src.includes('/video.'))) {
      GAEvent(GAEventNames.STORIES_VIDEO_CLOSE, {
        story_name: storyName,
        timeSec: (Date.now() - timeMs) / 1000,
        trigger: placement || '',
      });
    }
    onClose();
  }, [onClose, images, currentIndex, storyName]);


  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (e.target === modalRef.current) {
      handleClose();
    }
  }, [handleClose]);

  useEffect(() => {
    if (showMobileText) {
      GAEvent(GAEventNames.STORIES_PREVIEW_TEXT_ICON_MOBILE, {
        story_name: storyName,
        image_number: currentIndex + 1,
      });
    }
  }, [showMobileText]);

  const handleCloseMobileText = useCallback(() => {
    GAEvent(GAEventNames.STORIES_PREVIEW_TEXT_CLOSE_MOBILE, {
      story_name: storyName,
      image_number: currentIndex + 1,
    });
    setShowMobileText(false);
  }, [storyName, currentIndex]);

  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!isOpen) return;
    
    switch (e.key) {
      case 'Escape':
        handleClose();
        break;
      case 'ArrowLeft':
        handlePrevious();
        break;
      case 'ArrowRight':
        handleNext();
        break;
    }
  }, [isOpen, handleClose, handlePrevious, handleNext]);

  useEffect(() => {
    if (isOpen) {
      setTimeMs(Date.now());
      document.addEventListener('keydown', handleKeyDown);
    } else {
      document.removeEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, handleKeyDown]);

  // Subscribe to Bootstrap carousel slide event to sync index on swipe/touch
  useEffect(() => {
    if (!isOpen) return;
    const carouselEl = document.getElementById('newStoriesCarousel');
    if (!carouselEl) return;

    const onSlide = (event: any) => {
      if (typeof event?.to === 'number') {
        setCurrentIndex(event.to);
        return;
      }
      const targetEl = event?.relatedTarget as HTMLElement | null;
      const idxAttr = targetEl?.getAttribute?.('data-slide-index');
      if (idxAttr) {
        const parsed = parseInt(idxAttr, 10);
        if (!Number.isNaN(parsed)) setCurrentIndex(parsed);
      }
    };

    carouselEl.addEventListener('slide.bs.carousel', onSlide as EventListener);

    return () => {
      carouselEl.removeEventListener('slide.bs.carousel', onSlide as EventListener);
    };
  }, [isOpen]);

  const currentImage = useMemo<StoryImage>(() => unlockedImages[currentIndex], [unlockedImages, currentIndex]);
  const showPrevButton = useMemo<boolean>(() => currentIndex > 0, [currentIndex]);
  const showNextButton = useMemo<boolean>(() => currentIndex < unlockedImages.length - 1, [currentIndex, unlockedImages.length]);

  const items = useMemo(() => unlockedImages.map((image, index) => (
    <div key={image.id} className={`carousel-item stories-carousel-item`} data-slide-index={index}>
      <div className="preview-item-content">
        {image?.src.endsWith('webp') && <div className="preview-image-wrapper">
          <img 
            src={image?.src} 
            alt={image?.alt} 
            className="preview-image"
            style={{maxWidth: image?.previewText ? '85%' : '100%', maxHeight: 'calc(100vh - 80px)'}}
          />
          {image?.previewText && (
          <div 
            id="closing-ignore-el"
            className="preview-mobile-text-button text-icon"
            onClick={() => setShowMobileText(true)}
            style={{ 
              position: 'relative', 
              top: 'calc(50% - 15px)', 
              right: '60px', 
              color: 'white', 
              height: '48px', 
              width: '0',
              transform: 'translate(-75%, -50%)'
            }}
          >
            <div className="text-icon-bg">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <path d="M5 26V6C5 4.93913 5.42143 3.92172 6.17157 3.17157C6.92172 2.42143 7.93913 2 9 2H23C24.0609 2 25.0783 2.42143 25.8284 3.17157C26.5786 3.92172 27 4.93913 27 6V24.25C27 24.7141 26.8156 24.5796 26.4874 24.7437C26.1592 24.9078 25.7141 25 25.25 25H7C7 25.2652 7.21071 25.5196 7.58579 25.7071C7.96086 25.8946 8.46957 26 9 26H26C26.2652 26 26.5196 26.1054 26.7071 26.2929C26.8946 26.4804 27 26.7348 27 27C27 27.2652 26.8946 27.5196 26.7071 27.7071C26.5196 27.8946 26.2652 28 26 28H9C7.93913 28 6.92172 27.7893 6.17157 27.4142C5.42143 27.0391 5 26.5304 5 26ZM10.75 6C10.2859 6 9.84075 6.18437 9.51256 6.51256C9.18437 6.84075 9 7.28587 9 7.75V9.25C9 10.216 9.784 11 10.75 11H21.25C21.7141 11 22.1592 10.8156 22.4874 10.4874C22.8156 10.1592 23 9.71413 23 9.25V7.75C23 7.28587 22.8156 6.84075 22.4874 6.51256C22.1592 6.18437 21.7141 6 21.25 6H10.75Z" fill="white"></path>
            </svg>
            </div>
          </div>
        )}
        </div>}

        {!image?.src.endsWith('webp') && <video id="storyVideo" controls autoPlay>
            <source src={`${image?.src}.mp4`} type="video/mp4"/>
            <source src={`${image?.src}.mov`} type="video/quicktime"/>
            Your browser does not support the video tag.
        </video>}
        
        {image?.previewText && image?.src.endsWith('webp') && (
          <div className="preview-text-content">
            <h2 className="preview-title">{translate(image.previewText.titleKey)}</h2>
            <div className="preview-text">
              <p>{translate(image.previewText.textKey)}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )), [unlockedImages]);

  if (!isOpen || unlockedImages.length === 0) {
    return null;
  }

  return (
    <div 
      id="closing-ignore-el"
      ref={modalRef}
      className="image-preview-modal"
      onClick={handleBackdropClick}
    >
      <button 
        id="closing-ignore-el"
        className="preview-close" 
        onClick={handleClose}
        aria-label="Close"
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M18 6L6 18M6 6L18 18" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      <div id="newStoriesCarousel" style={{width: '100%'}} className="carousel slide" data-bs-touch="true" data-bs-ride="carousel" data-bs-interval="100000" data-bs-wrap="false">
        <div className="stories-carousel-inner">
          {items}
        </div>
      </div>
      
      {unlockedImages.length > 1 && (
          <>
            {showPrevButton && (
              <button 
                id="closing-ignore-el"
                className={`carousel-control-prev stories-carousel-control-btn`} 
                data-bs-target="#newStoriesCarousel"
                data-bs-slide="prev"
                type="button"
                onClick={handlePrevious}
              >
                <span className="carousel-control-prev-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Previous</span>
              </button>
            )}
            
            {showNextButton && (
              <button 
                id="closing-ignore-el"
                className={`carousel-control-next stories-carousel-control-btn`} 
                data-bs-target="#newStoriesCarousel"
                data-bs-slide="next"
                type="button"
                onClick={handleNext}
                style={showMobileText ? { height: `50px` } : {}}
              >
                <span className="carousel-control-next-icon" aria-hidden="true"></span>
                <span className="visually-hidden">Next</span>
              </button>
            )}
          </>
        )}

      {/* Mobile text overlay */}
      {currentImage?.previewText && (
        <div className={`preview-mobile-text ${showMobileText ? 'preview-mobile-text--opened' : 'preview-mobile-text--closed'}`}>
          <div className="preview-text-content preview-text-content--mobile">
            <div id="closing-ignore-el" onClick={handleCloseMobileText} className="preview-text-content-close--mobile">
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none">
              <g opacity="1">
              <path d="M23.1943 10.4171C23.6393 9.97216 23.6393 9.2507 23.1943 8.80571C22.7493 8.36073 22.0278 8.36073 21.5829 8.80571L16 14.3886L10.4171 8.80572C9.97216 8.36073 9.2507 8.36073 8.80571 8.80571C8.36073 9.2507 8.36073 9.97216 8.80571 10.4171L14.3886 16L8.80572 21.5829C8.36073 22.0278 8.36073 22.7493 8.80571 23.1943C9.2507 23.6393 9.97216 23.6393 10.4171 23.1943L16 17.6114L21.5829 23.1943C22.0278 23.6393 22.7493 23.6393 23.1943 23.1943C23.6393 22.7493 23.6393 22.0278 23.1943 21.5829L17.6114 16L23.1943 10.4171Z" fill="black"/>
              </g>
            </svg>
            </div>
            <h2 className="preview-title">{translate(currentImage.previewText.titleKey)}</h2>
            <div className="preview-text">
              <p>{translate(currentImage.previewText.textKey)}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImagePreviewModal;
